/**
 * EmailSettingsPage — Admin-only. Configures SMTP (Gmail by default).
 * Password is stored encrypted on backend, only shown as ******** on retrieval.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import PageHelp from "@/components/PageHelp";
import WeeklyWebsiteReportCard from "@/components/WeeklyWebsiteReportCard";
import { Loader2, Mail, Send, Save, Trash2, ExternalLink, CheckCircle, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailSettingsPage() {
  const [form, setForm] = useState({
    host: "smtp.gmail.com",
    port: 587,
    use_tls: true,
    username: "",
    password: "",
    from_email: "",
    from_name: "InvictaFlow",
  });
  const [configured, setConfigured] = useState(false);
  const [lastTest, setLastTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/email-settings`, { withCredentials: true })
      .then((r) => {
        const d = r.data;
        setForm((f) => ({
          ...f,
          host: d.host || f.host,
          port: d.port || f.port,
          use_tls: d.use_tls,
          username: d.username || "",
          from_email: d.from_email || "",
          from_name: d.from_name || "InvictaFlow",
          password: d.configured ? "********" : "",
        }));
        setConfigured(!!d.configured);
        setLastTest({
          at: d.last_test_at,
          ok: d.last_test_ok,
          error: d.last_test_error,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-settings`, form, { withCredentials: true });
      toast.success("Einstellungen gespeichert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
    setSaving(false);
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await axios.post(
        `${API}/admin/email-settings/test`,
        { to: testTo || undefined },
        { withCredentials: true }
      );
      toast.success(res.data.message);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fehlgeschlagen");
      load();
    }
    setTesting(false);
  };

  const wipe = async () => {
    if (!window.confirm("SMTP-Konfiguration wirklich entfernen?")) return;
    try {
      await axios.delete(`${API}/admin/email-settings`, { withCredentials: true });
      toast.success("Konfiguration entfernt");
      setForm({
        host: "smtp.gmail.com", port: 587, use_tls: true,
        username: "", password: "", from_email: "", from_name: "InvictaFlow",
      });
      setConfigured(false);
      setLastTest(null);
    } catch { toast.error("Fehler"); }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin" />;

  return (
    <div data-testid="email-settings-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight">E-Mail-Konfiguration</h1>
        <p className="text-[var(--studio-text-muted)] mt-1">
          SMTP-Server fuer automatische Welcome-Mails an neu erstellte Benutzer
        </p>
      </div>

      <PageHelp title="Wozu brauche ich die E-Mail-Konfiguration?" storageKey="email-settings">
        <p>
          Wenn ein neuer Benutzer in der Benutzerverwaltung angelegt wird <b>und du kein manuelles Passwort setzt</b>,
          erzeugt das System ein einmaliges Passwort und schickt es per E-Mail an den Benutzer. Diese E-Mail laeuft ueber den
          hier konfigurierten SMTP-Server. Gleiches gilt fuer Passwort-Reset-Mails und Inbox-Eskalations-Mails.
        </p>
        <h4>Wann brauche ich das nicht?</h4>
        <ul>
          <li>Wenn du beim Anlegen jedes Mal manuell ein Passwort setzt und es selbst weitergibst (z. B. ueber Signal/WhatsApp).</li>
          <li>Wenn alle Admins ihre Passwoerter bereits kennen.</li>
        </ul>
        <h4>Schritt-fuer-Schritt: Gmail-SMTP einrichten (empfohlen)</h4>
        <ol>
          <li>Bei Google: <b>Zwei-Faktor-Authentifizierung</b> aktivieren (Pflicht fuer App-Passwoerter).</li>
          <li>Auf <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">myaccount.google.com/apppasswords</a> ein neues App-Passwort fuer „Mail" erzeugen — du bekommst 16 Zeichen.</li>
          <li>Hier eintragen:
            <ul>
              <li><b>SMTP Host</b>: <code>smtp.gmail.com</code></li>
              <li><b>Port</b>: <code>587</code> (STARTTLS) oder <code>465</code> (SSL)</li>
              <li><b>Username</b>: deine Gmail-Adresse</li>
              <li><b>Passwort</b>: das 16-stellige App-Passwort (NICHT dein normales Gmail-Passwort)</li>
              <li><b>Absender</b>: z. B. <code>"InvictaFlow &lt;noreply@deinedomain.ch&gt;"</code></li>
            </ul>
          </li>
          <li><b>Test-Mail senden</b> → wenn sie in deinem Posteingang ankommt, ist alles OK.</li>
        </ol>
        <p>
          Alternativ: jedes andere SMTP (Microsoft 365, Mailgun, SendGrid, eigener Mailserver) funktioniert genauso —
          einfach Host/Port/Username/Passwort des Anbieters eintragen.
        </p>
      </PageHelp>

      <Card className="border-amber-200 bg-amber-50/30 shadow-none">
        <CardContent className="p-4">
          <p className="text-sm font-semibold mb-2">📧 Gmail-Setup (empfohlen)</p>
          <ol className="text-xs text-[var(--studio-text-muted)] space-y-1 list-decimal pl-5">
            <li>Stelle sicher, dass dein Gmail-Konto <b>2-Faktor-Authentifizierung</b> aktiviert hat</li>
            <li>
              Erstelle ein <b>App-Passwort</b> unter{" "}
              <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[#2563EB] underline inline-flex items-center gap-0.5">
                myaccount.google.com/apppasswords <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>Wähle "Mail" / "Andere" → "InvictaFlow" und kopiere das 16-stellige Passwort</li>
            <li>Trage es unten als <b>App-Passwort</b> ein (NICHT dein normales Gmail-Passwort)</li>
          </ol>
        </CardContent>
      </Card>

      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#2563EB]" /> SMTP-Server
            </CardTitle>
            {configured && <Badge className="bg-green-100 text-green-800 border-0">Konfiguriert</Badge>}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">SMTP-Host</Label>
              <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="smtp-host" />
            </div>
            <div>
              <Label className="text-xs">Port</Label>
              <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 587 })} data-testid="smtp-port" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={form.use_tls}
              onChange={(e) => setForm({ ...form, use_tls: e.target.checked })}
              className="w-4 h-4 accent-[#2563EB]"
              data-testid="smtp-tls"
            />
            STARTTLS verwenden (Port 587 = ja, Port 465 = nein/SSL direkt)
          </label>
          <div>
            <Label className="text-xs">Gmail-Adresse / Benutzername</Label>
            <Input
              type="email"
              value={form.username}
              onChange={(e) => {
                const v = e.target.value;
                setForm({ ...form, username: v, from_email: form.from_email || v });
              }}
              placeholder="dein-konto@gmail.com"
              data-testid="smtp-username"
            />
          </div>
          <div>
            <Label className="text-xs">App-Passwort *</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={configured ? "********" : "16-stelliges Gmail App-Passwort"}
              data-testid="smtp-password"
            />
            <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
              Wird verschluesselt gespeichert, niemals im Klartext zurueckgegeben.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Absender-E-Mail</Label>
              <Input
                type="email"
                value={form.from_email}
                onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                placeholder={form.username}
                data-testid="smtp-from-email"
              />
            </div>
            <div>
              <Label className="text-xs">Absender-Name</Label>
              <Input
                value={form.from_name}
                onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                data-testid="smtp-from-name"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="save-smtp-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Speichern
            </Button>
            {configured && (
              <Button variant="outline" onClick={wipe} className="text-red-500" data-testid="wipe-smtp-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Entfernen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {configured && (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardHeader>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Send className="w-4 h-4 text-[#2563EB]" /> Test-E-Mail senden
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Empfaenger (leer = aktueller Admin)</Label>
              <Input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="optional"
                data-testid="test-recipient"
              />
            </div>
            <Button
              onClick={test}
              disabled={testing}
              variant="outline"
              data-testid="test-smtp-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Test-E-Mail jetzt senden
            </Button>
            {lastTest?.at && (
              <div className="text-xs flex items-center gap-2" data-testid="last-test-status">
                {lastTest.ok ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Letzter Test erfolgreich: {new Date(lastTest.at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 text-red-600" />
                    <span className="text-red-700">Letzter Fehler: {lastTest.error || "unbekannt"}</span>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <WeeklyWebsiteReportCard />
    </div>
  );
}
